import { Injectable, inject, computed } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { TranslationService } from 'src/app/services/translation.service';
import { RegisterUserService, RegisterUserFormService } from '../../../../services';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { FormStepperService } from '../../../../components/register-user-form-mobile/services';
import { Interest } from '../../../../interfaces/register-user.response';
import { closeAllCiSnackbar } from '@iurban-malaga/cicerone-ui';

@Injectable({
  providedIn: 'root'
})
export class InterestsFieldService {
  // Injected services
  private _registerUserService = inject(RegisterUserService);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _formStepperService = inject(FormStepperService);
  private _translationService = inject(TranslationService);
  private _breakpointsService = inject(BreakpointsService);

  // Computed properties from services (maintaining exact same logic)
  private _interestList = this._registerUserService.registerUser().categories;
  private _currentInterestIdList = this._registerUserFormService.interests;
  private _markAllFormFieldsAsTouched = this._registerUserFormService.markAllFormFieldsAsTouched;
  private _isNextButtonClicked = this._formStepperService.isNextButtonClicked;
  private _selectedLanguage = this._translationService.selectedLanguage;
  private _isMobile = this._breakpointsService.isMobile;

  /**
   * Gets the ordered interest list with alphabetical sorting
   * Maintains the exact same logic as the original component
   * @returns Array of interests ordered alphabetically by label
   */
  getOrderedInterests(): Interest[] {
    return this._interestList
      .map(interest => {
        return {
          id: interest.id,
          value: interest.id,
          label: interest.name[this._selectedLanguage()],
          iconName: interest.icon
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Handles the chip set interests change event
   * Maintains the exact same logic as the original component
   * @param selectedInterests - Array of selected interest IDs
   * @param control - The form control to update
   */
  handleInterestsSelection(selectedInterests: number[], control: AbstractControl | null): void {
    // Exact same logic as original component
    this._registerUserFormService.setInterests(selectedInterests);
    
    control?.setValue(selectedInterests);
    control?.markAsTouched();
    control?.markAsDirty();
    
    if (this._isMobile()) {
      closeAllCiSnackbar();
    }
  }

  /**
   * Checks if a specific interest is currently selected
   * Maintains the exact same logic as the original component
   * @param id - Interest ID to check
   * @returns True if the interest is selected
   */
  isSelectedInterest(id: number): boolean {
    return this._currentInterestIdList().some(interestId => interestId === id);
  }

  /**
   * Validates if the field has errors and should display them
   * Maintains the exact same validation logic as the original component
   * @param control - The form control to validate
   * @returns True if field is invalid and should show errors
   */
  validateField(control: AbstractControl | null): boolean {
    if (!control) return false;
    
    const hasRequiredError = control.hasError('required');
    const hasMinArrayLengthError = control.hasError('minArrayLength');
    
    if (!hasRequiredError && !hasMinArrayLengthError) return false;
    
    // En mobile: solo mostrar error si el control específico ha sido tocado
    // Esto evita que errores de slides anteriores afecten a este campo
    if (this._isMobile()) {
      return (control.touched || control.dirty) && this._isNextButtonClicked();
    }
    
    // En desktop: comportamiento normal
    return control.touched || control.dirty;
  }

  /**
   * Marks form fields as touched for desktop validation
   * Maintains the exact same logic as the original component
   * @param control - The form control to mark as touched
   * @param cdr - Change detector reference for manual detection
   */
  markFieldsAsTouchedForDesktop(control: AbstractControl | null, cdr: any): void {
    if (this._markAllFormFieldsAsTouched()) {
      if (!control) return;
      
      // En mobile: NO marcar automáticamente como touched para evitar errores iniciales
      // Solo se marcará como touched cuando el usuario presione "Continuar" sin seleccionar
      if (this._isMobile()) {
        return;
      }
      
      control?.markAsTouched();
      control?.markAsDirty();

      // Trigger change detection manually for OnPush (exact same as original)
      cdr.detectChanges();
    }
  }
}
